import dayjs from 'dayjs';
import {generatePath, useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {useCreateTransaction} from '@/api/payments';
import {getGqlErrorMessage, isAccessRestrictionError} from '@/api/request';
import {FULL_DATE_FORMAT} from '@/constants';
import {PaymentProcess, PaymentTransactionStatus, PaymentType} from '@/enums';
import {useSession, useTossRequestBillingAuth} from '@/hooks';
import {ProductWithPriceInfo} from '@/interface';
import {paths} from '@/router';
import {GrayRoundedBox, PaddingBox, SimpleListBox, SimpleListBoxTitle} from '@/styled-components';
import {palette} from '@/styles';
import {Button} from '../Button';
import toast from '../HotToast';
import {Modal} from '../Modal';
import {Typography} from '../Typography';

interface IProps {
  duration: number;
  item: ProductWithPriceInfo;
  onClose?(): void;
}

export const TrialModal = ({duration, item, onClose}: IProps) => {
  const {mutate: createTransaction, isPending} = useCreateTransaction();
  const {user} = useSession();
  const customerKey = user?.id;
  const requestBillingAuth = useTossRequestBillingAuth(customerKey);
  const navigate = useNavigate();

  const onClick = async () => {
    if (!item || !customerKey) {
      return;
    }

    createTransaction(
      {
        customerKey,
        product: item.id,
        method: '카드',
      },
      {
        onSuccess: (data) => {
          const transaction = data.result;

          if (
            transaction?.type === PaymentType.Billing &&
            transaction?.status === PaymentTransactionStatus.PREPARE
          ) {
            requestBillingAuth(transaction, item);
            onClose?.();
          } else {
            navigate(
              {
                pathname: generatePath(paths.payments_process, {
                  transaction,
                  status: PaymentProcess.fail,
                }),
                search: `message=트랜잭션 생성 실패&code=${
                  transaction
                    ? `${transaction.type}/${transaction.status}`
                    : 'Not found transaction.'
                }`,
              },
              {replace: true}
            );
          }
        },
        onError: (error) => {
          // 이용 제한은 공통 에러 핸들러가 안내 모달을 띄운다.
          if (!isAccessRestrictionError(error)) {
            toast.error(getGqlErrorMessage(error));
          }
        },
      }
    );
  };

  const startDate = dayjs();
  const endDate = startDate.add(duration, 'day');

  return (
    <Modal
      title={`마이프차 지도 ${duration}일 무료 체험 시작`}
      width={328}
      onClose={onClose}
    >
      <Root>
        <Header>
          <Typography variant="bold24" align="center" color={palette.gray800}>
            ALL- IN-ONE
            <br />
            상권 분석 서비스
          </Typography>
          <Typography variant="medium14" color={palette.gray600}>
            내 인생을 결정할, 가장 중요한 비즈니스의 성공 확률을 마이프차 지도로
            높여보세요!
          </Typography>
        </Header>
        <RoundedBox>
          <Typography variant="medium14" fontWeight={400}>
            <strong>무료 체험 기간</strong> {startDate.format(FULL_DATE_FORMAT)}
            ~{endDate.format(FULL_DATE_FORMAT)}
          </Typography>
          <Typography variant="medium14" fontWeight={400}>
            <strong>결제 예정일</strong> {endDate.format(FULL_DATE_FORMAT)}
          </Typography>
        </RoundedBox>
        <SimpleListBox>
          <SimpleListBoxTitle>⚠ 안내사항</SimpleListBoxTitle>
          <ul>
            <li>결제 예정일에 등록한 결제 수단으로 요금이 자동 결제됩니다.</li>
            <li>
              구독 시작을 원하지 않으신다면 결제 예정일 전에 해지해 주셔야
              합니다.
            </li>
            <li>
              구독을 해지하셔도 {endDate.format(FULL_DATE_FORMAT)}까지 혜택은
              계속 이용하실 수 있습니다.
            </li>
          </ul>
        </SimpleListBox>
      </Root>
      <PaddingBox left={24} right={24} bottom={24}>
        <Button
          size="medium"
          color="secondary"
          variant="fill"
          fullWidth
          disabled={isPending}
          onClick={onClick}
        >
          {isPending ? '결제 요청 중...' : `${duration}일 무료 체험 시작하기`}
        </Button>
      </PaddingBox>
    </Modal>
  );
};

const Root = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const RoundedBox = styled(GrayRoundedBox)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: center;
  color: ${palette.gray800};
`;
